import { collection, getDocs, limit, orderBy, query, startAfter, where } from "firebase/firestore";
import { EntityFirebase } from "../common/entity.firebase";
import { RepositoryFirebase } from "../common/repository.firebase";
import { Product } from "./product.entity";
import { PagenedResult } from "../common/pagened-result";

class ProductRepository extends RepositoryFirebase<Product> {
    protected entity = Product;
    protected collection = 'products';

    /** Evita vários `getAll` enquanto o PDV debounce busca (substring em nome/descrição/código). */
    private sellSearchCatalogCache: { atMs: number; products: Product[] } | null = null;
    private static readonly SELL_SEARCH_CACHE_TTL_MS = 45_000;

    private async getCatalogSnapshotForSellSearch(): Promise<Product[]> {
        const now = Date.now();
        const c = this.sellSearchCatalogCache;
        if (
            c &&
            now - c.atMs < ProductRepository.SELL_SEARCH_CACHE_TTL_MS
        ) {
            return c.products;
        }
        const products = await this.getAll("name");
        this.sellSearchCatalogCache = { atMs: now, products };
        return products;
    }

    async findByName(prefix: string, limitTo: number = 10): Promise<Product[]> {
        const ref = this.getCollection();
        const q = query(ref, where("name", ">=", prefix), where("name", "<=", `${prefix}\uf8ff`), limit(limitTo));

        const querySnapshot = await getDocs(q);
        return await Promise.all(querySnapshot.docs.map(async(snap) => {
            return await Product.build(snap.id, snap.data());
        }));
    }

    /** Código explícito no documento (campo `code`). */
    async findByCode(code: string): Promise<Product | null> {
        const t = code.trim();
        if (!t) return null;
        const ref = this.getCollection();
        const q = query(ref, where("code", "==", t), limit(1));
        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) return null;
        const snap = querySnapshot.docs[0];
        return Product.build(snap.id, snap.data());
    }

    /**
     * Resolve leitura de código de barras / SKU: campo `code` ou documento legado cujo id era o código.
     */
    async findByCodeOrLegacyDocumentId(scan: string): Promise<Product | null> {
        const t = scan.trim();
        if (!t) return null;
        const byCode = await this.findByCode(t);
        if (byCode) return byCode;
        return this.findByIdOrNull(t);
    }

    async codeTakenByOther(code: string, excludeId: string): Promise<boolean> {
        const p = await this.findByCode(code);
        return p !== null && p.id !== excludeId;
    }

    /** Mesmo nome (ignorando maiúsculas), excluindo um id. */
    async findProductsWithSameName(name: string, excludeId: string): Promise<Product[]> {
        const target = name.trim().toLowerCase();
        if (target.length < 2) return [];
        const all = await this.getAll("name");
        return all.filter(
            (p) =>
                p.id !== excludeId &&
                p.getName().trim().toLowerCase() === target
        );
    }

    /**
     * Prefixos para Firestore: o índice é sensível a maiúsculas — "caderno" não acha "Caderno…".
     */
    private searchPrefixVariants(term: string): string[] {
        const t = term.trim();
        if (!t) return [];
        const titleCase = t
            .split(/\s+/)
            .filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" ");
        return Array.from(
            new Set([t, titleCase, t.toLowerCase(), t.toUpperCase()])
        ).filter((x) => x.length > 0);
    }

    /**
     * Mesma família de resultados da busca por nome no PDV, expandida:
     * vários prefixos (caso), palavras do termo, e varredura por trecho em nome, descrição, código e id.
     */
    async searchProductsForSell(term: string, limitTo: number = 30): Promise<Product[]> {
        const t = term.trim();
        if (!t) return [];
        const tl = t.toLowerCase();
        const seen = new Set<string>();
        const scored = new Map<string, { product: Product; score: number }>();
        const push = (p: Product, score: number) => {
            const id = p.id;
            if (seen.has(id)) {
                const prev = scored.get(id);
                if (prev && score > prev.score) {
                    scored.set(id, { product: p, score });
                }
                return;
            }
            seen.add(id);
            scored.set(id, { product: p, score });
        };

        const addPrefixHits = async (prefix: string, baseScore: number) => {
            if (!prefix) return;
            const hits = await this.findByName(prefix, Math.max(limitTo * 2, 40));
            for (const p of hits) {
                const name = (p.getName() || "").toLowerCase();
                let s = baseScore;
                if (name.startsWith(tl)) {
                    s += 80;
                } else if (name.includes(tl)) {
                    s += 40;
                }
                push(p, s);
            }
        };

        for (const pref of this.searchPrefixVariants(t)) {
            await addPrefixHits(pref, 60);
        }

        const words = t.split(/\s+/).filter((w) => w.length >= 2);
        for (const w of words) {
            for (const pref of this.searchPrefixVariants(w)) {
                await addPrefixHits(pref, 35);
            }
        }

        if (tl.length >= 2) {
            const all = await this.getCatalogSnapshotForSellSearch();
            for (const p of all) {
                const name = (p.getName() || "").toLowerCase();
                const desc = (p.getDescription() || "").toLowerCase();
                const code = (p.getCode() || "").toLowerCase();
                const id = p.id.toLowerCase();
                let s = 0;
                if (name.includes(tl)) {
                    s += name.startsWith(tl) ? 70 : 45;
                }
                if (desc.includes(tl)) {
                    s += 25;
                }
                if (code.includes(tl)) {
                    s += 35;
                }
                if (id.includes(tl)) {
                    s += 15;
                }
                if (s > 0) {
                    push(p, s);
                }
            }
        }

        const list = Array.from(scored.values())
            .sort((a, b) => b.score - a.score)
            .map((x) => x.product)
            .slice(0, limitTo);
        return list;
    }

    async getByCategoryId(categoryId: string, limitTo: number = 10) {
        const ref = this.getCollection();
        const q = query(ref, where("category", "==", categoryId), limit(limitTo));

        const querySnapshot = await getDocs(q);
        return await Promise.all(querySnapshot.docs.map(async(snap) => {
            return await Product.build(snap.id, snap.data());
        }));

    }

    public async getByCategoryIdPagened(categoryId: string, limitTo: number = 10) {
        const ref = this.getCollection();
        const querySnap = query(ref, orderBy('category'), limit(limitTo), where("category", "==", categoryId));
        const snapshot = await getDocs(querySnap);

        const items = await Promise.all(snapshot.docs.map(async(item) => {
            return await (this.entity as any).build(item.id, item.data());
        }));

        return new PagenedResult<Product>(
            items,
            snapshot.docs[0],
            snapshot.docs[snapshot.docs.length - 1],
            querySnap,
            limitTo,
            this.entity
        );
    }

    public async getBySupplierIdPagened(supplier: string, limitTo: number = 10) {
        const ref = this.getCollection();
        const querySnap = query(ref, orderBy('supplier'), limit(limitTo), where("supplier", "==", supplier));
        const snapshot = await getDocs(querySnap);

        const items = await Promise.all(snapshot.docs.map(async(item) => {
            return await (this.entity as any).build(item.id, item.data());
        }));

        return new PagenedResult<Product>(
            items,
            snapshot.docs[0],
            snapshot.docs[snapshot.docs.length - 1],
            querySnap,
            limitTo,
            this.entity
        );
    }

    /**
     * Retorna até 30 produtos para o copiloto de vendas.
     * Reutiliza o cache da busca do PDV quando disponível (TTL 45 s),
     * caso contrário faz uma query leve com limit.
     */
    async getCatalogForCopilot(): Promise<Product[]> {
        const c = this.sellSearchCatalogCache;
        if (c && Date.now() - c.atMs < ProductRepository.SELL_SEARCH_CACHE_TTL_MS) {
            return c.products.slice(0, 30);
        }
        const ref = this.getCollection();
        const q = query(ref, orderBy("name"), limit(30));
        const snap = await getDocs(q);
        return Promise.all(snap.docs.map(d => Product.build(d.id, d.data())));
    }
}

export const productRepository = new ProductRepository();